import isObject from 'lodash/isObject';
import GreenOption from './green-option';

/**
 * A PlanFuel's estimated usage is stored as a value repeated in all the fuel's
 * green options. So, we move it up to the PlanFuel's level instead. If no value
 * is able to be determined, this function returns `null`. If there is a value
 * to be returned, it is a plain-language `string` detailing the approximate
 * amount of energy this PlanFuel's pricing is based upon. An example might be:
 * `"4733 MJ / Monthly"`
 *
 * @param planInServerFormat
 * @returns {?string}
 */
function calculateEstimatedUsage(planInServerFormat) {
  if (!planInServerFormat) {
    return null;
  }

  if (!planInServerFormat.greenOption || !planInServerFormat.greenOption.length) {
    return null;
  }

  const valueInGreenOptions = planInServerFormat.greenOption.find((greenOption) => {
    return greenOption.estimatedCharge;
  });

  if (!valueInGreenOptions) {
    return null;
  }

  return valueInGreenOptions.estimatedCharge;
}

/**
 * @property {string} name
 * @property {string} productCode
 * @property {string} campaignId
 * @property {string} productId
 * @property {string} title
 * @property {string} description
 * @property {bool} isFromSolarCampaign
 * @property {string} solarFitText
 * @property {string} additionalTerms
 * @property {number} discount
 * @property {Array.<GreenOption>} greenOptions
 * @property {Object} rates
 * @property {Object} usage
 * @property {string} estimatedUsage
 */
export default class PlanFuel {
  /**
   * @param {Object.<string, string>} props - properties to own upon instantiation
   */
  constructor(props) {
    Object.assign(this, props);
  }

  /**
   * Create a new `PlanFuel` instance. The properties in `instanceSeed`
   * will be shallow-merged as properties of the new instance.
   *
   * @param {Object} instanceSeed
   * @return {PlanFuel}
   */
  static create(instanceSeed) {
    if (!instanceSeed) {
      throw new ReferenceError('missing required argument instanceSeed');
    }

    if (isObject(instanceSeed) === false) {
      throw new TypeError('argument instanceSeed was not of required type "Object"');
    }

    return new this(instanceSeed);
  }

  /**
   * @param {Object} rawInstance
   * @returns {PlanFuel}
   */
  static createFromServerFormat(rawInstance) {
    if (!rawInstance) {
      throw new ReferenceError('missing required argument rawInstance');
    }

    if (isObject(rawInstance) === false) {
      throw new TypeError('argument rawInstance was not of required type "Object"');
    }

    return PlanFuel.create({
      name: rawInstance.name,
      productCode: rawInstance.productCode,
      campaignId: rawInstance.campaignId,
      productId: rawInstance.productId,
      title: rawInstance.productTitle,
      description: rawInstance.description,
      isFromSolarCampaign: rawInstance.isSolarCampaign,
      solarFitText: rawInstance.solarFitText,
      additionalTerms: rawInstance.productExtraTerms,
      discount: rawInstance.discount,
      discountText: rawInstance.discountText,
      greenOptions: rawInstance.greenOption.map(
        GreenOption.createFromServerFormat.bind(GreenOption),
      ),
      tariff: rawInstance.tariff,
      usage: rawInstance.usage,
      estimatedUsage: calculateEstimatedUsage(rawInstance),
    });
  }
}
